import { DestroyRef, inject, Injectable, Signal, signal, WritableSignal } from "@angular/core";
import { LanguageDto, ProductKey, StorageService, TranslationService } from "gast-common-ng2";
import {
  PORTAL_LANGUAGE_SHOW_QUERY_PARAM_TOKEN,
  PORTAL_PRODUCT_OVERRIDDEN_LANGUAGES,
  PORTAL_PRODUCT_SERVICE_TOKEN,
  PortalProductLanguages,
  PortalQueryParams,
} from "../../constants";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { combineLatest, distinctUntilChanged, map, Observable, tap } from "rxjs";
import { LanguageModel } from "../../../header";
import { ActivatedRoute, NavigationExtras, ParamMap, Router } from "@angular/router";

type CurrentState = [ProductKey, ParamMap];

@Injectable()
export class PortalLanguageService {
  readonly #PRODUCT_OVERRIDDEN_LANGUAGES = inject(PORTAL_PRODUCT_OVERRIDDEN_LANGUAGES);
  readonly #PRODUCT_SHOW_LANG_IN_BROWSER = inject(PORTAL_LANGUAGE_SHOW_QUERY_PARAM_TOKEN);
  readonly #translationService = inject(TranslationService);
  readonly #portalProductService = inject(PORTAL_PRODUCT_SERVICE_TOKEN);
  readonly #storageService = inject(StorageService);
  readonly #activeRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #destroyRef = inject(DestroyRef);
  readonly #currentSelectedProduct: WritableSignal<ProductKey | undefined> = signal<ProductKey | undefined>(undefined);
  readonly #portalProductLanguages: Signal<Map<ProductKey, LanguageDto[]>> = this.#setPortalProductLanguages();

  readonly resolvePortalProductLanguages$: Observable<LanguageModel[]> =
    this.#portalProductService.selectedProduct$.pipe(
      takeUntilDestroyed(this.#destroyRef),
      tap((product: ProductKey) => this.#currentSelectedProduct.set(product)),
      map((product: ProductKey) => this.#portalProductLanguages().get(product) || []),
      map((languages: LanguageDto[]) => {
        return languages.map<LanguageModel>((l) => ({ visibleText: l.name, code: l.code?.toUpperCase() }));
      })
    );

  readonly resolvePortalProductLanguages: Signal<LanguageModel[]> = toSignal(this.resolvePortalProductLanguages$, {
    initialValue: [],
  });

  constructor() {
    combineLatest([this.#portalProductService.selectedProduct$, this.#activeRoute.queryParamMap])
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
      .subscribe((state: CurrentState) => {
        const queryParams: ParamMap = state[1];
        const langCode = queryParams.get(PortalQueryParams.LANGUAGE_CODE);
        this.setCurrentLanguageByCode(langCode);
      });
  }

  setCurrentLanguageByCode(code: string | null): void {
    if (this.#currentSelectedProduct()) {
      const prodLangCode = this.#findProductLanguageByCode(code);
      this.#translationService.changeLanguageUsingCode(prodLangCode);
      this.#storageService.setItem<string>(PortalQueryParams.LANGUAGE_CODE, prodLangCode);
      this.#updateBrowserRouteParam(prodLangCode);
    } else {
      throw new Error("Cannot set current language for unknown product");
    }
  }

  #findProductLanguageByCode(code: string | null): string {
    code = code ? code.toUpperCase() : null;
    const storageLastLangCode: string | null = this.#storageService.containsItem(PortalQueryParams.LANGUAGE_CODE)
      ? (this.#storageService.getItem(PortalQueryParams.LANGUAGE_CODE) as string)
      : null;
    const productLanguages = this.#portalProductLanguages().get(this.#currentSelectedProduct()!) || [];
    if (code && productLanguages.some((l) => l.code === code)) {
      const targetProductLanguage = productLanguages.find((l) => l.code === code)!;
      return targetProductLanguage.code.toLowerCase();
    } else if (storageLastLangCode && productLanguages.some((l) => l.code === storageLastLangCode.toUpperCase())) {
      const storageProductLanguage = productLanguages.find((l) => l.code === storageLastLangCode.toUpperCase())!;
      return storageProductLanguage.code.toLowerCase();
    } else {
      return productLanguages[0]
        ? productLanguages[0].code.toLowerCase()
        : this.#translationService.getDefaultLanguage().toLowerCase();
    }
  }

  #updateBrowserRouteParam(code: string) {
    if (this.#PRODUCT_SHOW_LANG_IN_BROWSER) {
      const options: NavigationExtras = {
        relativeTo: this.#activeRoute,
        queryParams: { [PortalQueryParams.LANGUAGE_CODE]: code },
        queryParamsHandling: "merge",
      };
      this.#router.navigate([], options).then(() => Promise.resolve());
    }
  }

  #setPortalProductLanguages(): Signal<Map<ProductKey, LanguageDto[]>> {
    const productLanguages = new Map<ProductKey, LanguageDto[]>();
    // override standard languages
    PortalProductLanguages.forEach((langArr: LanguageDto[], key: ProductKey) => {
      const overriddenProductLangs = this.#PRODUCT_OVERRIDDEN_LANGUAGES.find((o) => o.product === key);
      const resultLanguages =
        overriddenProductLangs && overriddenProductLangs.languages?.length > 0
          ? overriddenProductLangs.languages
          : langArr;
      productLanguages.set(
        key,
        resultLanguages.map((l) => ({ ...l, code: l.code?.toUpperCase() }))
      );
    });
    // add new products languages
    this.#addNewProductLanguages(productLanguages);
    return signal<Map<ProductKey, LanguageDto[]>>(productLanguages).asReadonly();
  }

  #addNewProductLanguages(productLanguages: Map<ProductKey, LanguageDto[]>) {
    const newProducts = new Set<ProductKey>(
      [...this.#PRODUCT_OVERRIDDEN_LANGUAGES.map((o) => o.product)].filter((prod) => !PortalProductLanguages.has(prod))
    );
    newProducts.forEach((product: ProductKey) => {
      const newProduct = this.#PRODUCT_OVERRIDDEN_LANGUAGES.find((o) => o.product === product);
      if (newProduct && newProduct.languages?.length > 0) {
        productLanguages.set(
          newProduct.product,
          newProduct.languages.map((l) => ({ ...l, code: l.code?.toUpperCase() }))
        );
      }
    });
  }
}
